<template>
  <div class="public-digital-agreement-page">
    <div class="container py-2 py-md-4">
      <b-card class="mb-2 mb-md-3">
        <h2 class="mb-1">{{ agreementTitle }}</h2>
        <p class="text-muted mb-0">
          Hello {{ partnerName }}, please review and complete this agreement.
        </p>
      </b-card>

      <b-card class="mb-2 mb-md-3">
        <b-alert v-if="successMessage" show variant="success" class="mb-2">
          {{ successMessage }}
        </b-alert>
        <b-alert v-if="errorMessage" show variant="danger" class="mb-2">
          {{ errorMessage }}
        </b-alert>

        <div v-if="loading" class="py-4 text-center text-muted">
          <b-spinner small class="mr-1" /> Loading agreement...
        </div>
        <template v-else-if="agreement">
          <agreement-pdf-scroller
            v-if="agreement.pdf_url"
            :pdf-url="agreement.pdf_url"
          />
          <b-alert v-else show variant="warning" class="mb-2">
            Agreement PDF is not available right now.
          </b-alert>
          <a v-if="agreement.pdf_url" :href="agreement.pdf_url" target="_blank" rel="noopener">Open PDF in new tab</a>
        </template>
      </b-card>

      <b-card v-if="agreement">
        <template v-if="isSigned">
          <b-alert show variant="success" class="mb-2">
            This agreement has already been signed.
          </b-alert>
          <img v-if="agreement.agreement_signature" :src="agreement.agreement_signature" alt="Agreement Signature" class="signature-preview">
        </template>
        <template v-else>
          <h5 class="mb-2">Agreement Actions</h5>
          <p class="text-muted mb-2">Please review the agreement above, then sign or cancel.</p>
          <div class="d-flex flex-wrap gap-2">
            <b-button variant="primary" @click="showSignModal = true">Sign Agreement</b-button>
            <b-button variant="secondary" @click="showCancelModal = true">Cancel Agreement</b-button>
          </div>
        </template>
      </b-card>
    </div>

    <b-modal
      v-model="showSignModal"
      title="Sign Agreement"
      size="lg"
      hide-footer
      @shown="onSignModalShown"
    >
      <p class="text-muted mb-2">Draw your signature below to confirm this agreement.</p>
      <digital-agreement-signature-pad ref="signaturePad" />
      <b-form-checkbox v-model="agreed" class="mt-2">
        I have read and agree to the terms stated in this agreement.
      </b-form-checkbox>
      <div class="d-flex flex-wrap mt-3">
        <b-button variant="outline-secondary" class="mr-1" @click="showSignModal = false">Close</b-button>
        <b-button variant="primary" :disabled="submitting" @click="submitSign">
          <b-spinner v-if="submitting" small class="mr-1" /> Submit Signature
        </b-button>
      </div>
    </b-modal>

    <b-modal
      v-model="showCancelModal"
      title="Cancel Agreement"
      size="lg"
      hide-footer
    >
      <p class="text-muted mb-2">Please share the reason for cancelling this agreement.</p>
      <b-form-textarea
        v-model="cancelReason"
        rows="4"
        placeholder="Enter cancellation reason..."
      />
      <div class="d-flex flex-wrap mt-3">
        <b-button variant="outline-secondary" class="mr-1" @click="showCancelModal = false">Close</b-button>
        <b-button variant="secondary" :disabled="submitting" @click="submitReject">
          <b-spinner v-if="submitting" small class="mr-1" /> Submit Reason
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BFormCheckbox,
  BFormTextarea,
  BModal,
  BSpinner
} from 'bootstrap-vue'
import AgreementPdfScroller from '@/components/AgreementPdfScroller.vue'
import DigitalAgreementSignaturePad from '@/components/DigitalAgreementSignaturePad.vue'
import {
  fetchChannelPartnerAgreement,
  rejectChannelPartnerAgreement,
  signChannelPartnerAgreement
} from '@/api/digitalAgreement'
import { channelPartnerAgreementTitle } from '@/utils/channelPartnerAgreement'

export default {
  name: 'PublicDigitalAgreement',
  components: {
    BAlert,
    BButton,
    BCard,
    BFormCheckbox,
    BFormTextarea,
    BModal,
    BSpinner,
    AgreementPdfScroller,
    DigitalAgreementSignaturePad
  },
  data () {
    return {
      loading: true,
      submitting: false,
      agreement: null,
      errorMessage: '',
      successMessage: '',
      showSignModal: false,
      showCancelModal: false,
      agreed: false,
      cancelReason: ''
    }
  },
  computed: {
    uuid () {
      return this.$route.params.uuid
    },
    isSigned () {
      return this.agreement?.status === 'signed'
    },
    partnerName () {
      return this.agreement?.name || 'Partner'
    },
    agreementTitle () {
      return this.agreement?.agreement_title || channelPartnerAgreementTitle(this.agreement?.useractivity || '')
    }
  },
  async mounted () {
    await this.loadAgreement()
  },
  methods: {
    async loadAgreement () {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await fetchChannelPartnerAgreement(this.uuid)
        this.agreement = response.data?.data || null
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Unable to load agreement.'
      } finally {
        this.loading = false
      }
    },
    onSignModalShown () {
      this.$nextTick(() => {
        const pad = this.$refs.signaturePad
        if (pad && typeof pad.initCanvas === 'function') {
          pad.initCanvas()
        }
      })
    },
    async submitSign () {
      if (!this.agreed) {
        this.errorMessage = 'Please confirm that you agree to the agreement terms.'
        return
      }
      const pad = this.$refs.signaturePad
      const signature = pad ? pad.getSignatureDataUrl() : null
      if (!signature) {
        this.errorMessage = 'Please draw your signature before submitting.'
        return
      }

      this.submitting = true
      this.errorMessage = ''
      try {
        const response = await signChannelPartnerAgreement(this.uuid, { signature })
        this.agreement = response.data?.data || this.agreement
        this.successMessage = response.data?.message || 'Agreement signed successfully.'
        this.showSignModal = false
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to sign agreement.'
      } finally {
        this.submitting = false
      }
    },
    async submitReject () {
      if (!this.cancelReason.trim()) {
        this.errorMessage = 'Please enter cancellation reason.'
        return
      }

      this.submitting = true
      this.errorMessage = ''
      try {
        const response = await rejectChannelPartnerAgreement(this.uuid, { cancel_reason: this.cancelReason.trim() })
        this.agreement = response.data?.data || this.agreement
        this.successMessage = response.data?.message || 'Reason submitted successfully.'
        this.showCancelModal = false
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to submit cancellation reason.'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.public-digital-agreement-page {
  width: 100%;
  min-height: 100vh;
  background: #f3f4f6;
}

.container {
  max-width: 980px;
}

.gap-2 {
  gap: 0.5rem;
}

.signature-preview {
  max-width: 320px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  padding: 8px;
}
</style>
